import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';

import GroupSelect from 'app/components/group/GroupSelect';

import useApi from 'app/context/useApi';

import { paper, divider } from 'app/styles/core';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    divider: {
        ...divider(theme)
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    switchContainer: {
        paddingLeft: 4,
        marginBottom: theme.spacing(1)
    },
    groupSelect: {
        backgroundColor: theme.palette.grey[100],
        width: '100%',
        height: '284px',
        overflowY: 'scroll',
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2)
    },
    buttonWrapper: {
        position: 'relative',
        marginTop: theme.spacing(3),
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
}))

export default function Membership(props) {

    const { urlPrefix, levels, levelsLoading, deleted } = props

    const classes = useStyles();
    const api = useApi();

    const [ association, setAssociation ] = useState({
        receive_emails: true,
        receive_sms: true,
        display_contact: true,
        display_home_address: true,
        display_work: true,
        display_work_contact: true,
        display_work_address: true,
        display_spouse: true,
        auto_renew: true,
        snailmail_to: 'home'
    });

    const [ levelId, setLevelId ] = useState(0);

    const [ associationLoading, setAssociationLoading ] = useState(false);
    const [ levelIdLoading, setLevelIdLoading ] = useState(false);
    const [ errors, setErrors ] = useState([]);

    // Getter functions ----------------------------------------

    const getAssociation = () => {
        setAssociationLoading(true);

        api.get(urlPrefix + '/association')
            .then(res => setAssociation(res.association))
            .catch(error => {
                //console.log(error.response);
            }).then(() => setAssociationLoading(false));
    }

    const getLevelId = () => {
        setLevelIdLoading(true);

        api.get(urlPrefix + '/level/id')
            .then(res => setLevelId(res.levelId))
            .catch(error => {
                //console.log(error.response);
            }).then(() => setLevelIdLoading(false));
    }

    useEffect(() => {
        getAssociation();
        getLevelId();
    }, []);

    const levelName = useMemo(() => {
        if (levelId > 0 && levels.length > 0) {
            const level = levels.find(l => l.id === levelId);
            return level ? level.title : 'Unassigned';
        } else return 'Loading...'
    }, [levelId, levels]);

    // Setter functions ------------------------------------------

    const handleChange = event => {
        const target = event.target;
        const name = target.name;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setAssociation({ ...association, [name]: value });
    };

    const handleLevelId = id => {
        setLevelId(id);
    }

    // Post functions ------------------------------------------

    const postAssociation = () => {
        setAssociationLoading(true);

        api.post(urlPrefix + '/association', association)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).then(() => setAssociationLoading(false));
    }

    const postLevel = () => {
        setLevelIdLoading(true);

        api.post(urlPrefix + '/level/id', { level_id: levelId})
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).then(() => setLevelIdLoading(false));
    }

    const isError = field => {
        return (errors[field]);
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    return (
        <form noValidate autoComplete="off">
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Membership
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Here administrators can adjust a users membership settings dictating how a user interacts with the club.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Grid container spacing={3}>
                    <Grid item sm={6} xs={12}>
                        <Typography gutterBottom variant="subtitle1">
                            Settings
                        </Typography>
                        <Divider className={classes.divider}/>
                        <Typography gutterBottom variant="subtitle2" color="textSecondary">
                            Communication
                        </Typography>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.receive_emails}
                                        onChange={handleChange}
                                        name="receive_emails"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Receives emails from this organization"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.receive_sms}
                                        onChange={handleChange}
                                        name="receive_sms"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Receives text messages from this organization"
                                margin="normal"
                            />
                        </div>
                        <Typography gutterBottom variant="subtitle2" color="textSecondary" className={classes.marginTop}>
                            Preferred Mailing Address
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup aria-label="gender" name="snailmail_to" value={association.snailmail_to} onChange={handleChange}>
                                <FormControlLabel value="home" control={<Radio size="small" color="primary"/>} label="Home Address" />
                                <FormControlLabel value="work" control={<Radio size="small" color="primary"/>} label="Business Address" />
                            </RadioGroup>
                        </FormControl>
                        <Typography
                            className={classes.marginTop}
                            gutterBottom
                            variant="subtitle2"
                            color="textSecondary"
                            >
                            Member Directory
                        </Typography>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.display_contact}
                                        onChange={handleChange}
                                        name="display_contact"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Display Home Contact Information"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.display_home_address}
                                        onChange={handleChange}
                                        name="display_home_address"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Display Home Address"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.display_work}
                                        onChange={handleChange}
                                        name="display_work"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Display Work Information"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.display_work_contact}
                                        onChange={handleChange}
                                        name="display_work_contact"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Display Work Contact Information"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.display_work_address}
                                        onChange={handleChange}
                                        name="display_work_address"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Display Work Address"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.switchContainer}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={association.display_spouse}
                                        onChange={handleChange}
                                        name="display_spouse"
                                        color="primary"
                                        disabled={deleted}
                                    />
                                }
                                label="Display Spouse Information"
                                margin="normal"
                            />
                        </div>
                        <div className={classes.buttonWrapper}>
                            <Button
                                color="primary"
                                variant="contained"
                                disabled={associationLoading || deleted}
                                onClick={postAssociation}
                                >
                                Update Settings
                            </Button>
                            {associationLoading &&
                                <CircularProgress size={24} className={classes.buttonProgress} />
                            }
                        </div>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <Typography gutterBottom variant="subtitle1">
                            Membership Level
                        </Typography>
                        <Divider className={classes.divider}/>
                        <Typography gutterBottom variant="subtitle2" color="textSecondary">
                            User is in the&nbsp;
                            <Typography gutterBottom variant="subtitle2" color="primary" component="span">
                                {levelName}
                            </Typography>
                            &nbsp;Level
                        </Typography>
                        <div className={classes.groupSelect}>
                            <GroupSelect
                                variant="level"
                                label="Select the Users Membership Level"
                                initGroupId={levelId}
                                onSelect={handleLevelId}
                                initGroupOptions={levels}
                                optionsLoading={levelsLoading}
                            />
                        </div>
                        <div className={classes.buttonWrapper}>
                            <Button
                                color="primary"
                                variant="contained"
                                disabled={levelIdLoading || deleted}
                                onClick={postLevel}
                                >
                                Update Membership Level
                            </Button>
                            {levelIdLoading &&
                                <CircularProgress size={24} className={classes.buttonProgress} />
                            }
                        </div>

                    </Grid>
                </Grid>
            </Paper>
        </form>
    )
}
